import { useState } from 'react';

import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { StarRating } from '#/components/StarRating/StarRating';
import { BodySmall } from '#/components/Text/BodySmall';
import { H3 } from '#/components/Text/H3';
import { Underline } from '#/components/Text/Underline';
import { useI18n } from '#/i18n/hooks/useI18n';
import { salariesSubmitReportFeedback } from '#/modules/salaries/api/salariesSubmitReportFeedback';
import type { SalariesSubmittedData } from '#/modules/salaries/types/SalariesSubmittedData';
import { gutterWidth } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { setError } from '#/zustand-stores/errorStore';

import {
  FeedbackBarContainer,
  FeedbackBarWrapper,
  StarRatingContainer,
} from './FeedbackBar.styled';

type Props = {
  submittedData: SalariesSubmittedData;
};

export function FeedbackBar({ submittedData }: Props) {
  const { i18n } = useI18n();

  const [rating, setRating] = useState<number | null>(null);
  const [showRating, setShowRating] = useState(true);

  const submitRating = (r: number) => {
    setRating(r);
    const {
      selectedSynonymId,
      city,
      stateCode,
      latitude,
      longitude,
      yearsOfExperience,
    } = submittedData;

    salariesSubmitReportFeedback({
      rating: r,
      selectedSynonymId,
      city,
      stateCode,
      latitude,
      longitude,
      yearsOfExperience,
    })
      .then(() => {
        setTimeout(() => {
          setShowRating(false);
        }, 1000);
      })
      .catch((e) => {
        setError(e);
        setRating(null);
      });
  };

  return (
    <FeedbackBarContainer data-qa-id="feedback-container">
      <FeedbackBarWrapper>
        <H3
          px={[gutterWidth, null, null, 0]}
          mb={[gutterWidth, null, null, '0']}
        >
          {rating
            ? i18n('Thank you for your {{s}}feedback!{{/s}}', {
                s: <Underline />,
              })
            : i18n(
                'How did this tool measure up to your {{s}}expectations{{/s}}?',
                { s: <Underline /> },
              )}
        </H3>
        <StarRatingContainer>
          {showRating ? (
            <StarRating
              disabled={Boolean(rating)}
              qaIdPrefix="rate-calculator"
              rating={rating}
              setRating={submitRating}
            />
          ) : (
            <BodySmall qaId="feedback-submitted">
              <StyledNativeLinkWithRef
                href="https://www.surveymonkey.com/r/idealist-explore-salaries"
                target="_blank"
                onClick={(event) =>
                  trackAnchorClicked(event, 'Salary Explorer Feedback Link')
                }
              >
                {i18n('Send additional feedback')}
              </StyledNativeLinkWithRef>
            </BodySmall>
          )}
        </StarRatingContainer>
      </FeedbackBarWrapper>
    </FeedbackBarContainer>
  );
}
